import { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import TimerIcon from '@mui/icons-material/AvTimer';
import moment from 'moment';

import styles from './styles';

const COUNTDOWN_SECONDS = 60;

/**
 * Countdown timer hook — decrements one whole second per tick.
 * @returns {number} remaining whole seconds
 */
export function useCountdown({ durationSeconds, active = true, onExpire }) {
  const [seconds, setSeconds] = useState(durationSeconds);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const intervalRef = useRef(null);
  const hasExpiredRef = useRef(false);

  useEffect(() => {
    setSeconds(durationSeconds);
    hasExpiredRef.current = false;
    if (!active) return undefined;

    intervalRef.current = setInterval(() => {
      setSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [durationSeconds, active]);

  // Run the expire side effect outside the state updater
  useEffect(() => {
    if (!active || seconds > 0 || hasExpiredRef.current) return;

    hasExpiredRef.current = true;
    clearInterval(intervalRef.current);
    onExpireRef.current?.();
  }, [seconds, active]);

  return seconds;
}

export default function CountdownTimer({ active, onExpire, durationSeconds = COUNTDOWN_SECONDS }) {
  const seconds = useCountdown({ durationSeconds, active, onExpire });

  return (
    <Box sx={{ ...styles.timer, ...(seconds <= 10 && styles.timerUrgent) }}>
      <TimerIcon sx={{ fontSize: 'inherit' }} />
      {moment.utc(seconds * 1000).format('mm:ss')}
    </Box>
  );
}
